import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import SkillsetCrest from './SkillsetCrest';
import { availableSkillGroups } from './availableSkillGroups';

const TIER_LABELS = ['Static', 'Basic', 'Advanced', 'Expert', 'Master', 'Grandmaster', 'Mythic'];

const skillRates = (skill, character) => {
    // Catalogs without secondary pricing use the primary rate in every placement.
    if (skill.secondaryBaseCost == null) {
        return [{ placement: 'all', label: 'All placements', base: skill.primaryBaseCost,
            increment: skill.primaryIncrementCost, current: true }];
    }
    const current = Number(skill.skillset) === Number(character?.skillsetPrimary) ? 'primary'
        : Number(skill.skillset) === Number(character?.skillsetSecondary) ? 'secondary' : 'tertiary';
    return ['primary', 'secondary', 'tertiary'].map(placement => ({
        placement, label: placement[0].toUpperCase() + placement.slice(1),
        base: skill[`${placement}BaseCost`], increment: skill[`${placement}IncrementCost`],
        current: placement === current,
    }));
};

function PurchasableSkillCard({ skill, owned, repeatable, ready, active, descriptionId, onPurchase, onRefund, onDescription, cost, rates }) {
    const timer = useRef();
    const longPressed = useRef(false);
    const clearTimer = () => clearTimeout(timer.current);
    useEffect(() => () => clearTimeout(timer.current), []);
    const max = !!owned && !repeatable;
    return <div className={`available-skill-card${owned ? ' is-owned' : ''}${active ? ' desc-active' : ''}`}>
        <button type="button" className="available-skill-purchase"
            data-skill-id={skill.skillId}
            aria-label={max ? `${skill.name}, maximum purchased` : owned ? `Add another rank of ${skill.name}` : `Add ${skill.name}`}
            aria-disabled={max || !ready}
            onClick={event => {
                if (longPressed.current) {
                    longPressed.current = false;
                    event.preventDefault();
                    return;
                }
                if (!max && ready) onPurchase(event);
            }}
            onContextMenu={event => {
                event.preventDefault();
                clearTimer();
                // Some touch browsers also dispatch contextmenu after long press.
                if (!longPressed.current && owned && ready) onRefund(event);
            }}
            onTouchStart={() => {
                clearTimer();
                longPressed.current = false;
                timer.current = setTimeout(() => {
                    longPressed.current = true;
                    onDescription();
                }, 500);
            }}
            onTouchEnd={clearTimer} onTouchMove={clearTimer} onTouchCancel={clearTimer}>
            <span className="available-skill-title">{skill.name}
                {owned && <span className="available-rank">{max ? 'Max' : `×${owned.ranks}`}</span>}
            </span>
            <span className="available-skill-metadata">
                <span className="available-skill-rates">
                    {rates.map(rate => {
                        const text = `[${rate.base}${Number(rate.increment) > 0 ? `+${rate.increment}` : ''}]`;
                        const label = `${rate.label} rate${rate.current ? ' (current)' : ''}: ${text}`;
                        return <span key={rate.placement} className={`available-skill-rate${rate.current ? ' is-current' : ''}`}
                            title={label} aria-label={label}>
                            {rate.current ? <strong>{text}</strong> : text}
                        </span>;
                    })}
                </span>
                {!max && <span className="available-skill-cost">{cost} SP</span>}
            </span>
        </button>
        <button type="button" className="available-description-toggle"
            data-description-skill-id={skill.skillId}
            aria-label={`Description of ${skill.name}`} aria-expanded={active}
            aria-controls={active ? descriptionId : undefined}
            onClick={onDescription}>i</button>
    </div>;
}

export default function AvailableSkills({ availableSkills, skillsetMap, activeSkillsetTab,
    setActiveSkillsetTab, currentCharacter, getSkillCost, getActionCost, handleDropSkill,
    onIncrement, onDecrement, catalogReady = true, touchDevice = false }) {
    const [expandedId, setExpandedId] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const root = useRef();
    const pendingFocus = useRef(null);
    const focusExpiry = useRef();
    useEffect(() => () => clearTimeout(focusExpiry.current), []);
    const instanceId = useId();
    const { grouped, keys } = availableSkillGroups(availableSkills, skillsetMap);
    const selected = grouped[activeSkillsetTab] || [];
    const ownedById = new Map((currentCharacter.skills || []).map(skill => [Number(skill.skillId), skill]));
    const label = skillsetMap[activeSkillsetTab] || `Skillset ${activeSkillsetTab}`;
    const toggleDescription = id => setExpandedId(prev => prev === id ? null : id);

    useEffect(() => { setExpandedId(null); setAnnouncement(''); }, [activeSkillsetTab, currentCharacter.characterId]);
    const expandedVisible = selected.some(skill => skill.skillId === expandedId);
    useEffect(() => { if (!expandedVisible) setExpandedId(null); }, [expandedVisible]);
    useLayoutEffect(() => {
        const pending = pendingFocus.current;
        if (!pending) return;
        pendingFocus.current = null;
        clearTimeout(focusExpiry.current);
        const button = root.current?.querySelector(`[data-skill-id="${pending.id}"]`);
        if (pending.restoreFocus) button?.focus();
        const owned = (currentCharacter.skills || []).find(skill => Number(skill.skillId) === Number(pending.id));
        const ranks = Number(owned?.ranks) || 0;
        setAnnouncement(`${pending.name}: ${ranks} rank${ranks === 1 ? '' : 's'} purchased.`);
    }, [currentCharacter.skills]);

    const trackAction = (event, skill) => {
        pendingFocus.current = { id: skill.skillId, name: skill.name,
            restoreFocus: event.currentTarget === document.activeElement };
        clearTimeout(focusExpiry.current);
        // A rejected action must not announce or move focus on a later edit.
        focusExpiry.current = setTimeout(() => { pendingFocus.current = null; }, 0);
    };

    const renderSkill = skill => {
        const owned = ownedById.get(Number(skill.skillId));
        const repeatable = [skill.primaryIncrementCost, skill.secondaryIncrementCost, skill.tertiaryIncrementCost].some(v => Number(v) > 0);
        const max = owned && !repeatable;
        const cost = max ? null : getActionCost
            ? getActionCost(owned ? { ...skill, ranks: Number(owned.ranks) } : skill, owned ? Number(owned.ranks) + 1 : 1)
            : getSkillCost(skill, 1);
        const descriptionId = `${instanceId}-description-${skill.skillId}`;
        return <React.Fragment key={skill.skillId}>
            <PurchasableSkillCard skill={skill} owned={owned} repeatable={repeatable} ready={catalogReady}
                active={expandedId === skill.skillId} descriptionId={descriptionId}
                cost={cost} rates={skillRates(skill, currentCharacter)}
                onDescription={() => toggleDescription(skill.skillId)}
                onPurchase={event => {
                    trackAction(event, skill);
                    if (owned) onIncrement?.(skill.skillId);
                    else handleDropSkill(skill);
                }}
                onRefund={event => {
                    trackAction(event, skill);
                    onDecrement?.(skill.skillId);
                }} />
            {expandedId === skill.skillId && <div id={descriptionId} className="available-desc-row"
                dangerouslySetInnerHTML={{ __html: skill.description || 'No description available.' }} />}
        </React.Fragment>;
    };
    // Keep each desktop pair together when a full-width description opens.
    // CSS restores card/description order within the pair on narrow screens.
    const renderRows = skills => {
        const rows = [];
        for (let i = 0; i < skills.length; i += 2) {
            rows.push(<div className="available-card-row" key={skills[i].skillId}>
                {skills.slice(i, i + 2).map(renderSkill)}
            </div>);
        }
        return rows;
    };
    const tiers = {};
    selected.forEach(skill => { (tiers[skill.tier ?? 0] ||= []).push(skill); });
    return <div className="available-skills-panel" ref={root}>
        <div className="available-panel-heading"><h2>Available Skills</h2>
            <span>{touchDevice ? 'Tap to add · i or long tap for description' : 'Click to add · Right-click to refund · i for description'}</span>
        </div>
        <div className="available-crest-grid" role="group" aria-label="Skillsets">
            {keys.map(key => {
                const name = skillsetMap[key] || `Skillset ${key}`;
                const count = grouped[key].filter(skill => ownedById.has(Number(skill.skillId))).length;
                return <button type="button" key={key} className="available-crest"
                    aria-label={name} title={name} aria-pressed={String(activeSkillsetTab) === key}
                    onClick={() => setActiveSkillsetTab(key)}>
                    {!!count && <span className="available-owned-count" title={`${count} purchased`}>{count}</span>}
                    <SkillsetCrest name={name} /><span>{name}</span>
                </button>;
            })}
        </div>
        {selected.length ? <>
            <div className="available-category-heading"><SkillsetCrest name={label} size={26} /><h3>{label}</h3></div>
            <div className="available-card-grid">
                {Number(activeSkillsetTab) === 8
                    ? Object.keys(tiers).sort((a, b) => Number(a) - Number(b)).map(tier => <React.Fragment key={tier}>
                        <h4 className="available-tier-heading">{TIER_LABELS[Number(tier)] || `Tier ${tier}`}</h4>
                        {renderRows(tiers[tier])}
                    </React.Fragment>) : renderRows(selected)}
            </div>
        </> : <p className="available-empty">{catalogReady ? 'No skills available in this category.' : 'Loading available skills…'}</p>}
        <span className="available-sr-only" role="status" aria-live="polite">{announcement}</span>
    </div>;
}
